import { useCallback, useRef, useState } from "react";

// Distance from the bottom (px) within which the view counts as "pinned". Forgiving of fractional-pixel
// rounding (hidpi/zoom) so a programmatic pin stays stuck, but small enough that any real scroll-up unpins.
const THRESHOLD_PX = 48;

/**
 * Keep a scroll container pinned to the bottom as content grows, but yield to the reader: once the user
 * scrolls up past {@link THRESHOLD_PX} the view unpins (`stickRef` → false) so appended content (e.g. a
 * streaming reply) no longer yanks them back; scrolling to within the threshold re-pins. Recomputing the
 * pin on every scroll means a programmatic pin lands at the bottom and stays stuck while a user scroll-up
 * unsticks — so no "ignore the programmatic scroll" bookkeeping is needed.
 *
 * `stickRef` is a ref (read it to gate a per-token autoscroll effect without re-rendering each token);
 * `atBottom` is state (drives a "jump to latest" affordance's visibility).
 */
export function useStickToBottom() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const stuck =
      el.scrollHeight - el.scrollTop - el.clientHeight < THRESHOLD_PX;
    stickRef.current = stuck;
    setAtBottom((prev) => (prev === stuck ? prev : stuck));
  }, []);

  // Instant ("auto") for the during-stream follow: a smooth animation emits intermediate scroll events at
  // non-bottom positions that would falsely unstick mid-animation. The jump button passes "smooth".
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollRef.current;
    stickRef.current = true;
    setAtBottom(true);
    el?.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  return { scrollRef, stickRef, onScroll, scrollToBottom, atBottom };
}
